// (C) Cloud Media Works, LLC - 2025 - All Rights Reserved
// By Drew Anderson

/**
 * Gets organization metadata from localStorage
 * @returns Organization metadata object or null if not found
 */
const getOrgMetadata = () => {
  try {
    const orgData = localStorage.getItem('org');
    return orgData ? JSON.parse(orgData) : null;
  } catch (error) {
    console.error('Error parsing org data from localStorage:', error);
    return null;
  }
};

/**
 * Gets settings from localStorage
 * @returns Settings object or null if not found
 */
const getSettings = () => {
  try {
    // Get the org data from localStorage
    const orgData = localStorage.getItem('org');
    if (!orgData) {
      console.warn('No org data found in localStorage');
      return null;
    }
    
    const org = JSON.parse(orgData);
    if (!org) {
      console.warn('Failed to parse org data');
      return null;
    }
    
    // Get assistant ID from URL or use default
    const urlParams = new URLSearchParams(window.location.search);
    const assistantId = urlParams.get('id') || 'admin-page';
    
    // [S2-B B2] The branding the page stored from the record (B1c's allowlisted botSettings
    // payload) — was a require() of the compiled settings.json. Only this bot's payload counts.
    let assistantSettings: any = null;
    try {
      const stored = JSON.parse(localStorage.getItem('botSettings') || 'null');
      if (stored && stored.botid === assistantId) assistantSettings = stored;
    } catch { /* unparseable ⇒ none */ }

    if (!assistantSettings) {
      console.warn(`No settings found for assistant ID: ${assistantId}`);
      return null;
    }
    
    // Create the final settings object with proper fallbacks
    const settings = {
      // Start with org data
      ...org,
      
      // Override with assistant-specific settings
      ...assistantSettings,
      
      // Ensure required fields with proper fallbacks
      name: assistantSettings.name || org.name || 'Assistant',
      description: assistantSettings.description || org.description || '',
      url: assistantSettings.homepage || org.homepage || window.location.origin,
      logo: assistantSettings.logo || org.logo || '',
      favicon: assistantSettings.favicon || org.favicon || '',
      
      // Add structured data for SEO
      applicationCategory: assistantSettings.applicationCategory || 'BusinessApplication',
      inLanguage: assistantSettings.inLanguage || 'en-US',
      softwareVersion: assistantSettings.softwareVersion || '1.0.0',
      
      // Add offers if available
      ...(assistantSettings.offers && { offers: assistantSettings.offers }),
      
      // Add creator/organization info
      creator: {
        '@type': 'Organization',
        name: org.name || assistantSettings.name || 'Assistant',
        url: org.homepage || assistantSettings.homepage || window.location.origin,
        ...(org.logo && { logo: org.logo })
      }
    };
    
    console.log('[SEO] Combined settings:', settings);
    return settings;
    
  } catch (error) {
    console.error('Error getting settings:', error);
    return null;
  }
};

/**
 * Gets the current assistant ID from localStorage
 * @returns Assistant ID or null if not found
 */
const getCurrentAssistantId = () => {
  try {
    return localStorage.getItem('selectedAssistantId');
  } catch (error) {
    console.error('Error getting assistant ID from localStorage:', error);
    return null;
  }
};

/**
 * Generates JSON-LD data for the current assistant
 * @returns Generated JSON-LD data or null if no metadata is available
 */
const generateAssistantJsonLd = (): Record<string, any> | null => {
  try {
    console.groupCollapsed('[SEO] Starting JSON-LD Generation');
    console.time('JSON-LD Generation Time');
    
    const settings = getSettings();
    console.log('[SEO] Retrieved settings:', settings);
    
    if (!settings) {
      console.warn('[SEO] No settings found in localStorage');
      console.groupEnd();
      return null;
    }

    // Get base URL from settings or use current origin as fallback
    const siteUrl = settings.url || window.location.origin;
    console.log('[SEO] Using site URL:', siteUrl);
    
    // Log all available settings for debugging
    console.log('[SEO] Available settings:', {
      name: settings.name,
      description: settings.description,
      applicationCategory: settings.applicationCategory,
      inLanguage: settings.inLanguage,
      softwareVersion: settings.softwareVersion,
      offers: settings.offers,
      creator: settings.creator,
      logo: settings.logo
    });
    
    // Construct the JSON-LD using the exact structure from settings
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: settings.name,
      description: settings.description,
      url: siteUrl,
      applicationCategory: settings.applicationCategory,
      inLanguage: settings.inLanguage,
      softwareVersion: settings.softwareVersion,
      operatingSystem: 'All',
      offers: {
        '@type': 'Offer',
        price: settings.offers?.price || '0',
        priceCurrency: settings.offers?.priceCurrency || 'USD'
      },
      creator: {
        '@type': 'Organization',
        name: settings.creator?.name || settings.name,
        url: settings.creator?.url || siteUrl
      },
      // Add logo URL if available
      ...(settings.logo && {
        image: new URL(settings.logo, siteUrl).toString(),
        screenshot: new URL(settings.logo, siteUrl).toString()
      })
    };

    console.log('[SEO] Generated JSON-LD:', JSON.parse(JSON.stringify(jsonLd)));
    console.timeEnd('JSON-LD Generation Time');
    console.groupEnd();
    return jsonLd;
  } catch (error) {
    console.error('Error generating JSON-LD:', error);
    return null;
  }
};

/**
 * Injects JSON-LD script into the document head
 * @param jsonLdContent The JSON-LD content to inject
 */
const injectJsonLd = (jsonLdContent: Record<string, any>) => {
  console.groupCollapsed('[SEO] Injecting JSON-LD');
  
  try {
    if (typeof document === 'undefined') {
      console.log('[SEO] Skipping injection during SSR');
      console.groupEnd();
      return;
    }

    const scriptId = 'assistant-ld-json';
    console.log(`[SEO] Script ID: ${scriptId}`);
    
    // Remove existing script if it exists
    const existingScript = document.getElementById(scriptId);
    if (existingScript && existingScript.parentNode) {
      console.log('[SEO] Removing existing JSON-LD script');
      existingScript.parentNode.removeChild(existingScript);
    } else {
      console.log('[SEO] No existing JSON-LD script found');
    }

    // Create and inject new script
    console.log('[SEO] Creating new JSON-LD script');
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    
    try {
      const jsonString = JSON.stringify(jsonLdContent, null, 2);
      script.text = jsonString;
      console.log('[SEO] JSON-LD content:', jsonString);
      
      // Ensure head exists
      if (!document.head) {
        throw new Error('Document head not found');
      }
      
      // Append to head
      document.head.appendChild(script);
      console.log('[SEO] Successfully injected JSON-LD into document head');
      
      // Verify injection
      const injectedScript = document.getElementById(scriptId);
      if (injectedScript && injectedScript.textContent) {
        console.log('[SEO] Verified JSON-LD script in DOM');
        console.log('[SEO] Script content length:', injectedScript.textContent.length);
      } else {
        console.warn('[SEO] Failed to verify JSON-LD script in DOM');
      }
    } catch (error) {
      console.error('[SEO] Error creating JSON-LD script:', error);
      throw error;
    }
  } catch (error) {
    console.error('[SEO] Error in JSON-LD injection:', error);
  } finally {
    console.groupEnd();
  }
};

/**
 * Injects JSON-LD data for the current assistant
 */
export const injectAssistantJsonLd = () => {
  console.groupCollapsed('[SEO] Starting JSON-LD Injection');
  console.time('Total Injection Time');
  
  try {
    if (typeof window === 'undefined') {
      console.log('[SEO] Skipping injection during SSR');
      console.groupEnd();
      return;
    }
    
    console.log('[SEO] Window object available, proceeding with injection');
    
    const jsonLdData = generateAssistantJsonLd();
    if (jsonLdData) {
      console.log('[SEO] Generated JSON-LD data, proceeding to injection');
      injectJsonLd(jsonLdData);
      console.log('[SEO] Successfully completed JSON-LD injection');
    } else {
      console.warn('[SEO] No JSON-LD data was generated');
    }
  } catch (error) {
    console.error('[SEO] Error in JSON-LD injection process:', error);
    // Log additional error details
    if (error instanceof Error) {
      console.error('[SEO] Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
    }
  } finally {
    console.timeEnd('Total Injection Time');
    console.groupEnd();
  }
};
